import React, { useState,useEffect } from "react";
import axiosInstance from "../AxiosInstance";
import SidebarLayout from "../SidebarLayout";
import InstructorSearchForm from "./InstructorSearchForm";
import InstructorTable from "./InstructorTable";
import InstructorPagination from "./InstructorPagination";
import InstructorDialog from "./InstructorDialog";
import { getNowInPst, getSubmissionStatus } from '../calendar/submissionUtils';
import { useNotification } from '../NotificationContext';
import { Container, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { saveAs } from 'file-saver';
import {jwtDecode} from 'jwt-decode';
import { throttle } from 'lodash';
import { motion } from "framer-motion";

const INSTRUCTOR_CACHE_KEY = "instructormain_state_v1";

const readInstructorCache = () => {
  try {
    const raw = sessionStorage.getItem(INSTRUCTOR_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const InstructorMain = () => {
  const cached = readInstructorCache();
  const [isDisabled, setIsDisabled] = useState(false);
  const [timeZone, setTimeZone] = useState('');
  const [state, setState] = useState(cached.state ?? '');
  const [instructors, setInstructors] = useState(Array.isArray(cached.instructors) ? cached.instructors : []);
  const [venues, setVenues] = useState([]);
  const [currentPage, setCurrentPage] = useState(cached.currentPage || 1);
  const [itemsPerPage] = useState(10);
  const [currentVenue, setCurrentVenue] = useState({});
  const [currentInstructor, setCurrentInstructor] = useState({});
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { showNotification } = useNotification();
  const [firstname, setFirstname] = useState(cached.firstname ?? '');
  const [lastname, setLastname] = useState(cached.lastname ?? '');
  const [city, setCity] = useState(cached.city ?? '');
  const [phonenumber, setPhonenumber] = useState(cached.phonenumber ?? '');
  const [totalPages, setTotalPages] = useState(cached.totalPages || 1);
  const [totalInstructors,setTotalInstructors]=useState(cached.totalInstructors || 0);
  const [selectedFile,setSelectedFile]=useState(null);
  const [venueId,setVenueId]=useState(cached.venueId ?? '');
  const token = localStorage.getItem("token"); 
  const [email,setEmail]=useState(cached.email ?? '');
  const [states,setStates]=useState([]);
  const [submissionFilter, setSubmissionFilter] = useState(cached.submissionFilter || 'ALL'); // ALL | YES | NO | EXPIRED
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictDetails, setConflictDetails] = useState([]);
  const refreshPageAfterEdit = () => {
    window.location.reload();
  };
  let userRole = null;
  let icpisname = null;
  let managerId = null;
  if (token) {
    const decodedToken = jwtDecode(token);
    userRole = decodedToken.claims?.role;
    icpisname = decodedToken.claims?.icpiename || decodedToken.claims?.firstname;
    managerId = decodedToken.claims?.id ?? null;
  }

  useEffect(() => {
    fetchInstructors();
    //fetchVenues();
    handleSearch();
    fetchStates();
  }, [currentPage]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        INSTRUCTOR_CACHE_KEY,
        JSON.stringify({
          state,
          instructors,
          currentPage,
          firstname,
          lastname,
          city,
          phonenumber,
          totalPages,
          totalInstructors,
          venueId,
          email,
          submissionFilter,
        }),
      );
    } catch {
      // ignore cache write errors
    }
  }, [
    state,
    instructors,
    currentPage,
    firstname,
    lastname,
    city,
    phonenumber,
    totalPages,
    totalInstructors,
    venueId,
    email,
    submissionFilter,
  ]);
  //获取venues信息
  const fetchVenues = async () => {
    try {
      const token = localStorage.getItem('token');
      //("Stored JWT in localStorage:", token);
      
      const authHeader = 'Bearer ' + token;
       // 判断 userRole 是否是 'ROLE_ICPIE'
    let queryParams = {};
    if (userRole !== 'ROLE_ICPIE') {
      queryParams = {
          role: userRole,
          icpisName: icpisname
      };
  } else {
      queryParams = { role: userRole }; // ROLE_ICPIE 也应该有 role 参数
  }
    const response = await axiosInstance.get('/venue/venuelistbyinstructor', {
      params: queryParams
      ,headers: {
        'Authorization': authHeader
      }
       // 传递参数
    });
      setInstructors(response.data); // 设置 instructors 数据
    } catch (error) {
      console.error('Error fetching instructors:', error);
    }
  };

  const fetchStates=async()=>{
    try{
      const response=await axiosInstance.get('/instructor/getallInstructorStates',{
        params:{
          role:userRole,
          icpisName:icpisname
        }})
      const data = Array.isArray(response.data) ? [...response.data] : [];
      data.sort();
      setStates(data);
    }
    catch(error)
    {
      console.error("Error fetching states:",error);
    }
  };
  //获取instructor
  const fetchInstructors = async () => {
    try {
   
      const response = await axiosInstance.get('/instructor', {
        params: {
          pageNum: currentPage,
          pageSize: itemsPerPage,
          city: city,
          role: userRole,
          icpisname:icpisname,
          state,
          phoneNumber: '',
          lastname,
          firstname,
          
        },
      });
      if (response.data.message === 'success') {
         const items = (response.data.data?.items || []).map(instructor => ({
          ...instructor,
          venueIds: instructor.venueIds || [],
          slackId: instructor.slackId ?? instructor.slack_id ?? '',
          submissionTime: instructor.submissionTime ?? instructor.submission_time ?? null,
        }));
        setInstructors(items);
       
      } else {
        showNotification('获取Instructor信息失败！', 'error');
      }
    } catch (error) {
      console.error('获取Instructor信息时出错:', error);
    }
  };
  // 执行搜索请求，支持传入覆盖参数（用于 Reset 时用空参数重新加载）
  const performSearch = (overrideParams = null) => {
    if (isLoading) return;
    setIsLoading(true);

    let params = overrideParams ?? {
      pageNum: currentPage,
      pageSize: itemsPerPage,
      role: userRole,
      icpisname: icpisname,
      city: city,
      state,
      venueId: venueId,
      phoneNumber: phonenumber,
      firstname: firstname,
      lastname: lastname,
      email: email,
      submittedFilter: submissionFilter,
    };

    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    });
    const token = localStorage.getItem('token');
    const authHeader = 'Bearer ' + token;
    axiosInstance.get('/instructor', {
      params,
      headers: { 'Authorization': authHeader }
    })
      .then(response => {
        if (response.data.message === "success") {
          const items = (response.data.data?.items || []).map(instructor => ({
            ...instructor,
            slackId: instructor.slackId ?? instructor.slack_id ?? '',
            submissionTime: instructor.submissionTime ?? instructor.submission_time ?? null,
          }));
          setInstructors(items);
          const total = response.data.totalElements ?? response.data.data?.totalElement ?? 0;
          setTotalInstructors(total);
          setTotalPages(Math.ceil(total / itemsPerPage) || 1);
        } else {
          showNotification('Instructor search failed!', 'error');
        }
      })
      .catch(error => {
        console.error('Error in request:', error);
        showNotification('Instructor search failed!', 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearch = () => performSearch();

  const handleAdd = () => {
    const newInstructor = {
      id: '',
      state: '',
      city: '',
      venueId: '',
      firstname: '',
      phoneNumber: '',
      email: '',
      slackId: '',
      wagePerHour: 0,
      totalClassTimes: 0,
      deposit: 0,
      rentManikinNumbers: 0,
      finance: 0,
      rentStatus: '',
      fobKey: ''
    };

    setCurrentInstructor(newInstructor);
    setIsEditMode(false);
    setOpen(true);
  };

  const handleClickOpen = (instructor) => {
    setCurrentInstructor(instructor);
    setOpen(true);
    setIsEditMode(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentInstructor(prev => ({ ...prev, [name]: value }));
  };
//保存instructor 信息
  const handleSave = throttle(() => {
    if(isDisabled)
      return;
    setIsDisabled(true);
   // 从 currentVenue 中提取 instructor 对象，并生成 instructorIds 列表
const venueIds = Array.isArray(currentInstructor.venues)
? currentInstructor.venues.map(venue => venue.id)
: (Array.isArray(currentInstructor.venueIds) ? currentInstructor.venueIds : []); // 提取 ID
const firstname=currentInstructor.firstname;
const lastname=currentInstructor.lastname;
const phonenumber=currentInstructor.phoneNumber;
const state=currentInstructor.state;
const city=currentInstructor.city;
const email=currentInstructor.email;
const slackId=currentInstructor.slackId;
const wageHour=currentInstructor.wageHour;
const salaryInfo=currentInstructor.salaryInfo;
const fobkey=currentInstructor.fobKey;
const finance=currentInstructor.finance;
const deposit=currentInstructor.deposit;
const rentManikinNumbers=currentInstructor.rentManikinNumbers;
const totalClassTimes=currentInstructor.totalClassTimes;
const rentStatus=currentInstructor.rentStatus;
// 构建符合 VenueDTO 格式的对象
const instructorDTO = {
  ...currentInstructor,
  venueIds: venueIds,  // 替换 instructor 对象为 ID 列表
  firstname:firstname,
  lastname:lastname,
  phonenumber:phonenumber,
  email:email,
  slackId:slackId,
  state:state,
  city:city,
  wageHour:wageHour,
  salaryInfo:salaryInfo,
  finance:finance,
  fobKey:fobkey,
  deposit:deposit,
  rentManikinNumbers:rentManikinNumbers,
  totalClassTimes:totalClassTimes,
  rentStatus:rentStatus,
};

    axiosInstance.put('/instructor', instructorDTO, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        setInstructors(prev => prev.map(v => v.id === currentVenue.id ? currentVenue : v));
        handleSearch();
        setOpen(false);
        setIsDisabled(false);
        showNotification('Instructor updated successfully!', 'success');
        console.log("venue information:",instructorDTO);
        refreshPageAfterEdit();
      })
      .catch(error => {
        console.error('Error in request:', error);
        const backendMessage = error?.response?.data?.message;
        if (error?.response?.status === 409 && backendMessage?.startsWith('SLOT_CONFLICT:')) {
          const details = backendMessage
            .split('\n')
            .slice(1)
            .map((line) => line.trim())
            .filter(Boolean);
          setConflictDetails(details);
          setConflictDialogOpen(true);
        } else {
          showNotification(backendMessage || 'Instructor update failed!', 'error');
        }
        setIsDisabled(false);
      });

      //后台更新instructor的信息
      //axiosInstance.
  },6000);
  //添加新的instructor
  const handleInsert = () => {
    const authHeader = 'Bearer ' + token;
    axiosInstance.post('/instructor', currentInstructor,  // 当前的 instructor 数据放在请求体中
      {
        headers: {
          'Authorization': authHeader  // 传递 Bearer token
        }
      }
   )
      .then(response => {
        setOpen(false);
        setCurrentPage(1);
        performSearch({ pageNum: 1, pageSize: itemsPerPage, role: userRole, icpisname: icpisname, city: city, state, phoneNumber: phonenumber, firstname: firstname, lastname: lastname, email: email, venueId: venueId });
        showNotification('Instructor added successfully!', 'success');
      })
      .catch(error => {
        console.error('Error in request:', error);
        showNotification('Instructor addition failed!', 'error');
      });
  };
  //删除instructor
  const handleDelete = () => {
    const confrimDelete=window.confirm("Are you sure you want to delete this instructor. This action cannot be undone.");
    if(confrimDelete){
    axiosInstance.delete('/instructor', { params: { instructorId: currentInstructor.id } })
      .then(response => {
        setOpen(false);
        handleSearch();
        showNotification('Instructor deleted!', 'success');
      })
      .catch(error => {
        console.error('Error in request', error);
        showNotification('Instructor deletion failed!', 'error');
      });
    }
  };
  //重置搜索条件并重新加载数据
  const handleReset = () => {
    setState('');
    setFirstname('');
    setLastname('');
    setEmail('');
    setPhonenumber('');
    setCity('');
    setVenueId('');
    setSubmissionFilter('ALL');
    setCurrentPage(1);
    performSearch({
      pageNum: 1,
      pageSize: itemsPerPage,
      role: userRole,
      icpisname: icpisname,
      submittedFilter: 'ALL',
    });
  };
 //翻页
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    //handleSearch();
  };

  //导出文件
  const handleExport=async()=>{
    try{
      const response=await axiosInstance.get('/instructor/export',{
        responseType:'blob',
      });

      const contentDisposition=(await response).headers['content-disposition'];
      const filename=contentDisposition
                ?contentDisposition.split('filename')[1].split(';')[0].replace(/"/g,''):'instructors.xlsx';
      
      //console.log('Content-Disposition:', contentDisposition);     
         //文件下载
    saveAs(new Blob([response.data]),filename);
  } catch(error){
    console.error('Error exported data',error);
  }
 };
   

 //上传instructor文件
  //上传文件到后端
  const handleFileChange=(event)=>{
    setSelectedFile(event.target.files[0]);
    event.target.value = null;
};

const handleUpload=()=>{
    if(!selectedFile){
      showNotification("Please select a file first!","error");
      return;
    }
    const formData=new FormData();
    formData.append('file',selectedFile);

    axiosInstance.post('/instructor/import',formData,{
      headers:{
        'Content-Type':'multipart/form-data'
      }
    })
    .then(response=>{
      //console.log(response.data);

    })
    .catch(error=>{
      console.log(error);
    });
};

  return (
    <SidebarLayout>
      <motion.div 
        initial={{opacity:0, y:50}}
        animate={{opacity:1, y:0}}
        exit={{opacity:0,y:-50}}
        transition={{duration:0.5}}
        >
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <InstructorSearchForm
          city={city}
          setCity={setCity}
          state={state}
          setState={setState}
          venueId={venueId}
          setVenueId={setVenueId}
          email={email}
          setEmail={setEmail}
          firstname={firstname}
          setFirstname={setFirstname}
          lastname={lastname}
          setLastname={setLastname}
          phonenumber={phonenumber}
          setPhonenumber={setPhonenumber}
          submissionFilter={submissionFilter}
          setSubmissionFilter={setSubmissionFilter}
            onSubmissionFilterChange={(value) => {
            setSubmissionFilter(value);
            setCurrentPage(1);
            performSearch({
              pageNum: 1,
              pageSize: itemsPerPage,
              role: userRole,
              icpisname: icpisname,
              submittedFilter: value,
              city,
              state,
              venueId,
              firstname,
              lastname,
              email,
            });
          }}
          handleSearch={handleSearch}
          handleReset={handleReset}
          handleExport={handleExport}
          handleFileChange={handleFileChange}
          handleUpload={handleUpload}
          selectedFile={selectedFile}
          states={states}
          setCity={setCity}
        />
        <InstructorTable currentItems={instructors} handleClickOpen={handleClickOpen} userRole={userRole} icpisName={icpisname} submissionFilter={submissionFilter} managerId={managerId} />
        <InstructorPagination totalPages={totalPages} currentPage={currentPage} handlePageChange={handlePageChange} />
        <Button variant="contained" color="primary" onClick={handleAdd} sx={{ mt: 2 }}>
          Add Instructor
        </Button>
      </Container>
      <InstructorDialog
        open={open}
        handleClose={handleClose}
        isEditMode={isEditMode}
        currentInstructor={currentInstructor}
        handleChange={handleChange}
        handleSave={handleSave}
        handleInsert={handleInsert}
        handleDelete={handleDelete}
        userRole={userRole}
        managerId={managerId}
        setIsDisabled={setIsDisabled}
        isDisabled={isDisabled}
      />
      <Dialog
        open={conflictDialogOpen}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setConflictDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Slot Conflict Detected</DialogTitle>
        <DialogContent dividers>
          {conflictDetails.length > 0 ? (
            conflictDetails.map((detail, idx) => (
              <div key={`${detail}-${idx}`}>{detail}</div>
            ))
          ) : (
            <div>Conflicting slot found. No data was saved.</div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setConflictDialogOpen(false)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      </motion.div>
    </SidebarLayout>
  );
};

export default InstructorMain;